import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Alert, Badge, Box, Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnConfiguratorDrawer } from '../../../components/ui/columnConfigurator/ColumnConfiguratorDrawer';
import { useNavigate } from 'react-router-dom';
import { TableTemplate } from '../../../components/ui/tableTemplate/TableTemplate';
import { TablePaginationBar } from '../../../components/ui/TablePaginationBar';
import { useColumnConfigurator } from '../../../hooks/useColumnConfigurator';
import { useDefaultEmployeeColumns } from '../hooks/employeeColumns';
import { useGetEmployees } from '../hooks/useGetEmployees';
import { useEmployeeFilterOptions } from '../hooks/useEmployeeFilterOptions';
import { useTableState } from '../../../hooks/useTableState';
import { useDraftFilters } from '../../../hooks/useDraftFilters';
import type { EmployeeRow } from '../employeeTypes';
import { EMPLOYEE_COLUMN_GROUPS } from '../columnGroups';
import { EmployeeFilterDrawer } from './EmployeeFilterDrawer';
import { EMPLOYEE_ADVANCED_FILTER_KEYS } from './employeeFilterSections';

// v2: default order revised + `contract_current` made sticky (must stay in the
// contiguous leading sticky block). Bumped so stale saved orders — which placed
// `contract_current` last — don't break the left-pin offsets.
const COLUMN_STORAGE_KEY = 'homere:employees:columnOrder:v2';

export function EmployeeTable() {
  const { t } = useTranslation('common');
  const filterOptions = useEmployeeFilterOptions();
  const allColumns = useDefaultEmployeeColumns(filterOptions);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  const {
    visibleColumns,
    visibleKeys,
    isOpen: isConfiguratorOpen,
    openConfigurator,
    cancelConfigurator,
    applyConfigurator,
    saveViewConfigurator,
    toggleColumn,
    resetToDefaults,
    selectedCount,
    effectiveMax,
    isAtMax,
  } = useColumnConfigurator(allColumns, { storageKey: COLUMN_STORAGE_KEY });

  const navigate = useNavigate();
  const {
    sortState,
    handleSortChange,
    filters,
    debouncedFilters,
    activeFilterKeys,
    setFilter,
    clearFilter,
    clearAll,
    page,
    limit,
    setPage,
    setLimit,
  } = useTableState<EmployeeRow>();

  // Deferred layer for the advanced panel: it edits a draft; only Apply commits
  // (merged) onto the live filters. The header column-filter row stays live.
  const {
    draft,
    setDraftFilter,
    clearDraftFilter,
    apply: applyDraft,
    reset: resetDraft,
    reseed: reseedDraft,
    hasUnapplied,
  } = useDraftFilters<EmployeeRow>(filters, EMPLOYEE_ADVANCED_FILTER_KEYS, setFilter, clearFilter);

  const openFilterPanel = () => {
    reseedDraft(); // draft mirrors the currently applied filters on open
    setFilterPanelOpen(true);
  };

  // allColumns provides the full databaseName mapping for filters, even on hidden columns.
  // visibleColumns is used only for table display and row mapping.
  const { data, loading, error, meta } = useGetEmployees(
    allColumns,
    sortState,
    debouncedFilters,
    page,
    limit,
  );

  const activeFilterCount = activeFilterKeys.size;

  const toolbarActions = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {activeFilterCount > 0 && (
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<FilterListOffIcon />}
          onClick={() => {
            clearAll();
            resetDraft();
          }}
          sx={{ borderRadius: 5, textTransform: 'none' }}
        >
          {t('table.clearFilters')} ({activeFilterCount})
        </Button>
      )}
      <Badge
        badgeContent={activeFilterCount}
        color="error"
        invisible={activeFilterCount === 0}
        sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
      >
        <Button
          variant={filterPanelOpen ? 'contained' : 'outlined'}
          size="small"
          startIcon={<FilterListIcon />}
          onClick={() => (filterPanelOpen ? setFilterPanelOpen(false) : openFilterPanel())}
          sx={{ borderRadius: 5, textTransform: 'none' }}
        >
          {t('employeeList.filters.buttonLabel')}
        </Button>
      </Badge>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnIcon />}
        onClick={openConfigurator}
        sx={{ borderRadius: 5, textTransform: 'none' }}
      >
        {t('columnConfigurator.buttonLabel')}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {error && (
        <Alert severity="warning" sx={{ mb: 1, flexShrink: 0 }}>
          {t('employeeList.dataUnavailable')}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, flexShrink: 0 }}>
        {toolbarActions}
      </Box>
      {/* Deferred: the drawer edits `draft`; the table refetches only on Apply. */}
      <EmployeeFilterDrawer
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        onApply={applyDraft}
        onReset={resetDraft}
        hasUnapplied={hasUnapplied}
        allColumns={allColumns}
        filterState={draft}
        onFilterChange={setDraftFilter}
        onFilterClear={clearDraftFilter}
      />
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableTemplate<EmployeeRow>
          columns={visibleColumns}
          rows={data}
          striped
          stickyHeader
          fillHeight
          loading={loading}
          emptyStateMessage={t('employeeList.noDataFound')}
          sortState={sortState}
          onSortChange={handleSortChange}
          filterState={filters}
          onFilterChange={setFilter}
          onFilterClear={clearFilter}
          pagination={
            meta && (
              <TablePaginationBar
                meta={meta}
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            )
          }
          onRowClick={row => navigate(`/employees/${row.hc_uuid_key}`)}
        />
      </Box>
      <ColumnConfiguratorDrawer
        open={isConfiguratorOpen}
        onCancel={cancelConfigurator}
        onApply={applyConfigurator}
        onSaveView={saveViewConfigurator}
        allColumns={allColumns}
        columnGroups={EMPLOYEE_COLUMN_GROUPS}
        visibleKeys={visibleKeys}
        onToggle={toggleColumn}
        onResetToDefaults={resetToDefaults}
        selectedCount={selectedCount}
        effectiveMax={effectiveMax}
        isAtMax={isAtMax}
      />
    </Box>
  );
}
